// src/components/ImportAdsAdmin.jsx
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API_DOMAIN } from "config";
import { Heading, Img } from "../../components";

export default function ImportAdsAdmin() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [lastRunAt, setLastRunAt] = useState(null);
  const [meta, setMeta] = useState(null);
  const [cliStarted, setCliStarted] = useState(false);
  const [cliRunning, setCliRunning] = useState(false);

  // Admin
  const [adminKey, setAdminKey] = useState("");
  const [userId, setUserId] = useState("1");

  // UI
  const [keyVisible, setKeyVisible] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedTech, setCopiedTech] = useState(false);
  const [showTech, setShowTech] = useState(false);

  // Progress UX
  const [progress, setProgress] = useState({ pct: 0, stage: "idle", hint: "" });
  const progressTimerRef = useRef(null);

  const TEST_LIMIT = 1;

  useEffect(() => {
    const savedKey = localStorage.getItem("import_ads_admin_key") || "";
    const savedUserId = localStorage.getItem("import_ads_user_id") || "1";
    setAdminKey(savedKey);
    setUserId(savedUserId);
  }, []);

  const baseUrl = useMemo(() => `${API_DOMAIN}/importCars/importAds`, []);
  const triggerCliUrl = useMemo(() => `${API_DOMAIN}/importCars/triggerCliImport`, []);
const testMailUrl = useMemo(() => `${API_DOMAIN}/importCars/sendTestImportMail`, []);
const runTestMail = async () => {
  setLoading(true);
  setError("");
  setResult(null);
  setMeta(null);
  setShowTech(false);

  const key = adminKey?.trim();
  if (!key) {
    setLoading(false);
    setError("חסר Admin Key . הכניסי מפתח והריצי שוב.");
    return;
  }

  // UX
  setProgress({ pct: 10, stage: "request", hint: "שולח מייל בדיקה…" });
  if (progressTimerRef.current) clearInterval(progressTimerRef.current);

  try {
    const startedAt = Date.now();

    // אפשר גם GET, אבל נשאיר POST כמו אצלך
    const res = await axios.post(
      testMailUrl,
      {},
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Admin-Key": key,
        },
        timeout: 0,
      }
    );

    const tookMs = Date.now() - startedAt;

    setResult(res.data);
    setMeta({
      status: res.status,
      tookMs,
      url: testMailUrl,
      mode: "test_mail",
      limit: null,
    });
    setLastRunAt(new Date());

    setProgress({ pct: 100, stage: "done", hint: "נשלח מייל בדיקה" });
  } catch (e) {
    setError(normalizeError(e));
    setProgress({ pct: 100, stage: "failed", hint: "נכשל לשלוח מייל בדיקה" });
  } finally {
    setLoading(false);
  }
};

  // ✅ build URL with params (user_id + optional flags + limit)
const buildUrl = useCallback(
  ({ skipImages = true, limit = 20, from = 0, doMot = false, doCleanup = true } = {}) => {
    const cleanUserId = String(userId || "").trim();
    const params = new URLSearchParams();

    if (cleanUserId) params.set("user_id", cleanUserId);

    params.set("from", String(Math.max(0, Number(from) || 0)));
    params.set("limit", String(Math.max(1, Number(limit) || 20)));

    if (skipImages) params.set("skip_images", "1");
    if (doMot) params.set("do_mot", "1");
    if (doCleanup) params.set("do_cleanup", "1");

    // ✅ תמיד לשלוח מייל בסוף הייבוא המלא
    params.set("mail_on_done", "1");

    return `${baseUrl}?${params.toString()}`;
  },
  [baseUrl, userId]
);
  const urlDefault = useMemo(() => buildUrl({ skipImages: false }), [buildUrl]);
  const urlNoImages = useMemo(() => buildUrl({ skipImages: true }), [buildUrl]);
  const urlTest5NoImages = useMemo(() => buildUrl({ skipImages: true, limit: TEST_LIMIT }), [buildUrl]);
  const urlTest5Full = useMemo(() => buildUrl({ skipImages: false, limit: TEST_LIMIT }), [buildUrl]);

  const normalizeError = (e) => {
    if (!e?.response) {
      return e?.message || "Network/CORS error: אין תשובה מהשרת (בדקי Console + Network)";
    }
    const status = e.response.status;
    const data = e.response.data;
    const text =
      (typeof data === "string" && data) ||
      data?.error ||
      data?.message ||
      (data ? JSON.stringify(data, null, 2) : "");
    return `HTTP ${status}\n${text}`;
  };

  const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
  const pick = (obj, keys, fallback = undefined) => {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return fallback;
  };

  const fmtNum = (v) => {
    const n = Number(v);
    if (Number.isFinite(n)) return n.toLocaleString("he-IL");
    return v ?? "—";
  };

  const formatSeconds = (ms) => `${Math.round((ms || 0) / 1000)}s`;

  // --- Result normalization (for nice UI) ---
  const normalizeImportResult = (raw) => {
    if (Array.isArray(raw)) {
      return {
        kind: "array",
        total: raw.length,
        items: raw,
        summary: {
          total: raw.length,
          inserted: null,
          updated: null,
          skipped: null,
          errorsCount: 0,
        },
        errors: [],
      };
    }
    if (!isObj(raw)) {
      return { kind: "unknown", raw, items: [], errors: [], summary: {} };
    }

    const root = raw;
    const preview = pick(root, ["preview", "result", "data"], root);

    const errors = pick(preview, ["errors", "Errors"], pick(root, ["errors"], []));
    const images = pick(preview, ["images", "Images"], pick(root, ["images"], null));

    const total = pick(preview, ["total", "count", "api_total"], pick(root, ["total", "count", "api_total"], null));
    const inserted = pick(preview, ["inserted", "created"], pick(root, ["inserted", "created"], null));
    const updated = pick(preview, ["updated"], pick(root, ["updated"], null));
    const skipped = pick(preview, ["skipped"], pick(root, ["skipped"], null));
    const success = pick(preview, ["success", "ok"], pick(root, ["success", "ok"], null));

    const itemsCandidate =
      pick(preview, ["items"], null) ||
      pick(root, ["items"], null) ||
      pick(preview, ["cars", "Cars", "ads", "Ads"], null) ||
      pick(root, ["cars", "Cars", "ads", "Ads"], null);

    const items = Array.isArray(itemsCandidate) ? itemsCandidate : [];

    return {
      kind: "object",
      success,
      total,
      inserted,
      updated,
      skipped,
      images,
      errors: Array.isArray(errors) ? errors : [],
      items,
      raw,
      summary: {
        total,
        inserted,
        updated,
        skipped,
        errorsCount: Array.isArray(errors) ? errors.length : 0,
      },
    };
  };

  const normalized = useMemo(() => normalizeImportResult(result), [result]);

  // --- Try to extract image urls from anywhere (professional gallery) ---
  const collectImageUrls = (raw, max = 18) => {
    const out = [];
    const seen = new Set();

    const push = (u) => {
      if (!u || typeof u !== "string") return;
      const url = u.trim();
      if (!url) return;
      if (seen.has(url)) return;
      if (!/\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i.test(url) && !/^https?:\/\//i.test(url)) {
        return;
      }
      seen.add(url);
      out.push(url);
    };

    const walk = (v) => {
      if (!v || out.length >= max) return;
      if (typeof v === "string") {
        if (/^https?:\/\//i.test(v) || /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(v)) push(v);
        return;
      }
      if (Array.isArray(v)) {
        for (const x of v) walk(x);
        return;
      }
      if (typeof v === "object") {
        for (const k of ["url", "src", "image", "image_url", "img", "thumbnail", "thumb", "path"]) {
          if (v[k]) walk(v[k]);
        }
        for (const key of Object.keys(v)) {
          if (out.length >= max) break;
          walk(v[key]);
        }
      }
    };

    walk(raw);
    return out.slice(0, max);
  };

  const imageUrls = useMemo(() => collectImageUrls(result), [result]);

  // --- Progress UX (stage-based) ---
  const startProgressUx = (skipImages) => {
    setProgress({ pct: 5, stage: "starting", hint: "מתחיל הרצה…" });

    if (progressTimerRef.current) clearInterval(progressTimerRef.current);

    const stages = skipImages
      ? [
          { pct: 12, stage: "request", hint: "שולח בקשה לשרת…" },
          { pct: 32, stage: "fetching", hint: "מושך מודעות…" },
          { pct: 62, stage: "processing", hint: "מייבא ומעדכן נתונים…" },
          { pct: 86, stage: "finalizing", hint: "מסכם ומחזיר תוצאות…" },
        ]
      : [
          { pct: 12, stage: "request", hint: "שולח בקשה לשרת…" },
          { pct: 28, stage: "fetching", hint: "מושך מודעות…" },
          { pct: 52, stage: "processing", hint: "מייבא ומעדכן נתונים…" },
          { pct: 72, stage: "syncImages", hint: "מסנכרן תמונות…" },
          { pct: 86, stage: "finalizing", hint: "מסכם ומחזיר תוצאות…" },
        ];

    let i = 0;
    progressTimerRef.current = setInterval(() => {
      setProgress((p) => {
        if (p.pct >= 90) return p;
        const s = stages[Math.min(i, stages.length - 1)];
        i++;
        return { pct: s.pct, stage: s.stage, hint: s.hint };
      });
    }, 1400);
  };

  const finishProgressUx = (ok) => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    setProgress({
      pct: 100,
      stage: ok ? "done" : "failed",
      hint: ok ? "הושלם בהצלחה" : "נכשל",
    });
  };

  const copyToClipboard = async (text, setFlag) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlag(true);
      setTimeout(() => setFlag(false), 1500);
    } catch {}
  };

  const clearAll = () => {
    setError("");
    setResult(null);
    setMeta(null);
    setShowTech(false);
    setCliStarted(false);
    setCliRunning(false);
    setProgress({ pct: 0, stage: "idle", hint: "" });
  };

// ✅ הפעלת פול יבוא ברקע דרך full_import_cli.php
const runCliImport = async () => {
  setLoading(true);
  setError("");
  setResult(null);
  setMeta(null);
  setCliStarted(false);
  setCliRunning(false);
  setShowTech(false);

  const key = adminKey?.trim();
  if (!key) {
    setLoading(false);
    setError("חסר Admin Key . הכנס מפתח ונסה שוב.");
    return;
  }

  setProgress({ pct: 20, stage: "request", hint: "מפעיל יבוא..." });

  try {
    const res = await axios.post(
      triggerCliUrl,
      {},
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Admin-Key": key,
        },
        timeout: 15000,
      }
    );

    setProgress({ pct: 100, stage: "done", hint: "הופעל בהצלחה" });

    if (res.data?.running) {
      setCliRunning(true);
    } else {
      setCliStarted(true);
      setLastRunAt(new Date());
    }
  } catch (e) {
    setError(normalizeError(e));
    setProgress({ pct: 100, stage: "failed", hint: "נכשל" });
  } finally {
    setLoading(false);
  }
};
// ✅ run import (optionally without images / with limit)
const runImport = async ({ skipImages = true, limit = 20, doMot = false } = {}) => {
  setLoading(true);
  setError("");
  setResult(null);
  setMeta(null);
  setShowTech(false);

  const key = adminKey?.trim();
  if (!key) {
    setLoading(false);
    setError("חסר Admin Key . הכניסי מפתח והריצי שוב.");
    return;
  }

  startProgressUx(skipImages);

  let from = 0;
  let last = null;
  const startedAt = Date.now();

  try {
    while (true) {
      const targetUrl = buildUrl({ skipImages, limit, from, doMot, doCleanup: true });

      const res = await axios.post(
        targetUrl,
        {}, // גוף JSON קטן (לא POST ריק)
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Admin-Key": key,
          },
          timeout: 0,
        }
      );

      last = res.data;
      setResult(last);

      const apiTotal = Number(last?.api_total || 0);
      const nextFrom = Number(last?.next_from ?? (from + Number(last?.processed || 0)));

      if (apiTotal > 0) {
        const pct = Math.min(99, Math.max(1, Math.round((nextFrom / apiTotal) * 100)));
        setProgress({ pct, stage: "processing", hint: `מייבא… ${nextFrom}/${apiTotal}` });
      }

      if (last?.done) {
        const tookMs = Date.now() - startedAt;
        setMeta({
          status: res.status,
          tookMs,
          url: targetUrl,
          mode: skipImages ? "no_images" : "full",
          limit,
        });
        setLastRunAt(new Date());
        finishProgressUx(true);
        break;
      }

      from = nextFrom;

      // הפסקה קטנה כדי לא להעמיס על השרת
      await new Promise((r) => setTimeout(r, 250));
    }
  } catch (e) {
    setError(normalizeError(e));
    finishProgressUx(false);
  } finally {
    setLoading(false);
  }
};


  const isKeyMissing = !adminKey?.trim();

  // ---------- Styles ----------
  const shell = { direction: "rtl", textAlign: "right" };

  const card = {
    border: "1px solid #e6e7ea",
    borderRadius: 22,
    background: "#fff",
    boxShadow: "0 1px 0 rgba(0,0,0,0.02), 0 12px 28px rgba(15, 23, 42, 0.07)",
    overflow: "hidden",
  };

  const section = { padding: 16 };
  const sectionBorderTop = { ...section, borderTop: "1px solid #f0f1f3" };
  const label = { fontSize: 12, color: "#6b7280", marginBottom: 6 };

  const input = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #d1d5db",
    outline: "none",
    background: "#fff",
    fontSize: 14,
  };

  const inputLtr = { ...input, direction: "ltr", textAlign: "left" };

  const row = {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-start",
  };

  const btnBase = {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #d1d5db",
    background: "#fff",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    userSelect: "none",
  };

  const btnPrimary = (disabled) => ({
    ...btnBase,
    background: disabled ? "#f3f4f6" : "#0f172a",
    borderColor: disabled ? "#e5e7eb" : "#0f172a",
    color: disabled ? "#9ca3af" : "#fff",
    cursor: disabled ? "not-allowed" : "pointer",
    minWidth: 210,
    justifyContent: "center",
  });

  const btnSecondary = (disabled) => ({
    ...btnBase,
    background: disabled ? "#f3f4f6" : "#fff",
    borderColor: disabled ? "#e5e7eb" : "#0f172a",
    color: disabled ? "#9ca3af" : "#0f172a",
    cursor: disabled ? "not-allowed" : "pointer",
    minWidth: 210,
    justifyContent: "center",
  });

  const chip = (type) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 10px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 13,
    fontWeight: 900,
    background:
      type === "loading"
        ? "#fffbeb"
        : type === "error"
        ? "#fef2f2"
        : type === "success"
        ? "#f0fdf4"
        : "#f8fafc",
    color:
      type === "loading"
        ? "#92400e"
        : type === "error"
        ? "#991b1b"
        : type === "success"
        ? "#166534"
        : "#334155",
    borderColor:
      type === "loading"
        ? "#fde68a"
        : type === "error"
        ? "#fecaca"
        : type === "success"
        ? "#bbf7d0"
        : "#e2e8f0",
  });

  const dot = (type) => ({
    width: 8,
    height: 8,
    borderRadius: 999,
    background:
      type === "loading"
        ? "#f59e0b"
        : type === "error"
        ? "#ef4444"
        : type === "success"
        ? "#16a34a"
        : "#94a3b8",
  });

  const notice = (type) => ({
    borderRadius: 14,
    padding: 12,
    border: "1px solid",
    background: type === "warn" ? "#fffbeb" : type === "error" ? "#fef2f2" : "#f0fdf4",
    borderColor: type === "warn" ? "#fde68a" : type === "error" ? "#fecaca" : "#bbf7d0",
    color: type === "warn" ? "#92400e" : type === "error" ? "#991b1b" : "#166534",
    fontSize: 13,
  });

  const kpiCard = {
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 14,
    background: "#fff",
    minWidth: 150,
    flex: "1 1 170px",
  };

  const kpiLabel = { fontSize: 12, color: "#64748b", marginBottom: 6, fontWeight: 700 };
  const kpiValue = { fontSize: 20, fontWeight: 950, color: "#0f172a" };

  const divider = { height: 1, background: "#eef2f7", margin: "14px 0" };

  const statusType = loading ? "loading" : error ? "error" : result ? "success" : "ready";
  const statusText = loading ? "מריץ" : error ? "שגיאה" : result ? "הצלחה" : "מוכן";

  // --- UI components ---
  const ProgressBar = ({ pct, hint }) => (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
        <div style={{ fontSize: 13, color: "#334155", fontWeight: 800 }}>{hint || "מכין…"}</div>
        <div style={{ fontSize: 13, color: "#64748b", fontWeight: 900 }}>{pct}%</div>
      </div>
      <div
        style={{
          marginTop: 8,
          height: 10,
          borderRadius: 999,
          background: "#eef2ff",
          overflow: "hidden",
          border: "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, pct))}%`,
            height: "100%",
            background: "#c7d2fe",
            transition: "width 420ms ease",
          }}
        />
      </div>
    </div>
  );

  const KPI = ({ label: l, value }) => (
    <div style={kpiCard}>
      <div style={kpiLabel}>{l}</div>
      <div style={kpiValue}>{value}</div>
    </div>
  );

  const ImageGrid = ({ urls }) => {
    if (!urls?.length) return null;
    return (
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 950, color: "#0f172a", marginBottom: 10 }}>
          תמונות שנמצאו ({urls.length})
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 10 }}>
          {urls.map((u, i) => (
            <a
              key={i}
              href={u}
              target="_blank"
              rel="noreferrer"
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                overflow: "hidden",
                background: "#fff",
                display: "block",
                boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
              }}
              title="פתח תמונה בלשונית חדשה"
            >
              <div style={{ aspectRatio: "1 / 1", background: "#f8fafc" }}>
                <img
                  src={u}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
              <div
                style={{
                  padding: 8,
                  fontSize: 11,
                  color: "#64748b",
                  direction: "ltr",
                  textAlign: "left",
                  wordBreak: "break-all",
                }}
              >
                {u}
              </div>
            </a>
          ))}
        </div>
      </div>
    );
  };

  const ErrorTable = ({ rows }) => {
    if (!rows?.length) return null;
    return (
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 950, marginBottom: 10, color: "#991b1b" }}>
          שגיאות ({rows.length})
        </div>
        <div style={{ overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 16, background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", direction: "rtl" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                <th style={{ textAlign: "right", padding: 10, fontSize: 12, color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>#</th>
                <th style={{ textAlign: "right", padding: 10, fontSize: 12, color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>מס׳ רכב</th>
                <th style={{ textAlign: "right", padding: 10, fontSize: 12, color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>שגיאה</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx}>
                  <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#64748b" }}>{idx + 1}</td>
                  <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9", fontSize: 13, fontWeight: 900, color: "#0f172a" }}>
                    {r.license_plate || r.plate || r.car_number || "—"}
                  </td>
                  <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9", fontSize: 13, color: "#0f172a", whiteSpace: "pre-wrap" }}>
                    {r.error || r.message || JSON.stringify(r)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const ItemCards = ({ items }) => {
    if (!items?.length) return null;
    return (
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 950, color: "#0f172a", marginBottom: 10 }}>
          פריטים ({items.length})
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
          {items.slice(0, 24).map((it, idx) => {
            const title =
              it.title ||
              it.name ||
              it.model ||
              it.car_name ||
              `${it.manufacturer || ""} ${it.model || ""}`.trim() ||
              "פריט";
            const plate = it.license_plate || it.plate || it.car_number || it.CarID || it.pcfcarlicenseplate;
            const status = it.status || it.action || it.result || it.state;

            const thumb = collectImageUrls(it, 1)?.[0];

            return (
              <div
                key={idx}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 18,
                  background: "#fff",
                  overflow: "hidden",
                  boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
                }}
              >
                {thumb ? (
                  <div style={{ aspectRatio: "16 / 9", background: "#f8fafc" }}>
                    <img
                      src={thumb}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      aspectRatio: "16 / 9",
                      background: "#f8fafc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#94a3b8",
                      fontWeight: 900,
                    }}
                  >
                    אין תמונה
                  </div>
                )}

                <div style={{ padding: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 950, color: "#0f172a" }}>{title}</div>
                  <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {plate && (
                      <span style={{ ...chip("ready"), fontWeight: 900, background: "#f8fafc" }}>
                        מס׳ רכב: <span style={{ direction: "ltr" }}>{plate}</span>
                      </span>
                    )}
                    {status && (
                      <span style={{ ...chip("ready"), fontWeight: 900 }}>
                        סטטוס: <span style={{ direction: "ltr" }}>{String(status)}</span>
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: 10, fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
                    {it.remote_id && (
                      <div>
                        remote_id: <b style={{ color: "#0f172a", direction: "ltr" }}>{String(it.remote_id)}</b>
                      </div>
                    )}
                    {it.modifiedon && (
                      <div>
                        modified: <b style={{ color: "#0f172a", direction: "ltr" }}>{String(it.modifiedon)}</b>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {items.length > 24 && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
            מוצגים 24 פריטים ראשונים (כדי לשמור על ביצועים).
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="flex bg-blue_gray-50 p-8 sm:p-4" style={{ width: "94%", paddingTop: "1rem", ...shell }}>
        <div className="w-[100%] items-start gap-4 md:flex-col" style={{ gap: "2%" }}>
          <div className="flex relative flex-1 flex-col gap-8 self-center rounded-[24px] bg-secondary-primary_white p-6 md:self-stretch sm:p-4 h-[calc(100vh_-_3rem)]">
            <header className="flex flex-col items-start gap-3">
              <div className="flex items-center justify-between gap-5 mt-5 self-stretch border-b border-solid border-gray-200 md:flex-col pb-4">
                <div className="flex items-center gap-3 w-full">
                  <Heading size="heading9xl" as="h4" className="!font-heebo text-[25px] font-bold whitespace-nowrap lg:text-[21px]">
                    יבוא רכבים
                  </Heading>

                  <Img src="/images/img_user_red_a700_03_1.svg" alt="Car" className="mr-auto" />
                  <div className="w-full flex justify-center gap-4" />
                  <Img src="/images/ad-123.svg" alt="User" />
                </div>
              </div>
            </header>
            {/*
<button
  type="button"
  style={btnSecondary(loading || isKeyMissing)}
  onClick={runTestMail}
  disabled={loading || isKeyMissing}
  title={isKeyMissing ? "חסר Admin Key" : ""}
>
  {loading ? "מריץ…" : "שלח מייל בדיקה"}
</button>*/}

            <Suspense fallback={<div className="text-center text-gray-500 py-4">…טוען…</div>}>
              <div style={{ ...card, marginTop: 12 }}>
                {/* Header */}
                <div style={{ ...section, borderBottom: "1px solid #f0f1f3" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    {/* status chip */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={chip(statusType)}>
                        <span style={dot(statusType)} />
                        {statusText}
                      </span>

                      {lastRunAt && (
                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          הרצה אחרונה:{" "}
                          <b style={{ color: "#0f172a" }}>
                            {lastRunAt.toLocaleString("he-IL")}
                          </b>
                        </span>
                      )}
                    </div>

                    {/* buttons */}
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        style={btnPrimary(loading || isKeyMissing)}
                        onClick={runCliImport}
                        disabled={loading || isKeyMissing}
                        title={isKeyMissing ? "חסר Admin Key" : ""}
                      >
                        {loading ? "מפעיל..." : "הרץ יבוא עכשיו"}
                      </button>

           
            

                      <button type="button" style={btnBase} onClick={clearAll} disabled={loading}>
                        נקה
                      </button>
                    </div>
                  </div>

                  {isKeyMissing && (
                    <div style={{ marginTop: 12, ...notice("warn") }}>
                      חסר Admin Key . הכנס מפתח כדי לאפשר הרצה.
                    </div>
                  )}

                  {loading && <ProgressBar pct={progress.pct} hint={progress.hint} />}
                </div>

                {/* Inputs */}
                <div style={section}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: 12, alignItems: "start" }}>
                    <div>
                      <div style={label}>Admin Key (X-Admin-Key)</div>
                      <div style={row}>
                        <input
                          value={adminKey}
                          onChange={(e) => {
                            const v = e.target.value;
                            setAdminKey(v);
                            localStorage.setItem("import_ads_admin_key", v);
                          }}
                          type={keyVisible ? "text" : "password"}
                          placeholder="הדביקי כאן את המפתח"
                          style={inputLtr}
                        />
                        <button type="button" style={btnBase} onClick={() => setKeyVisible((p) => !p)}>
                          {keyVisible ? "הסתר" : "הצג"}
                        </button>
                      </div>
                    </div>

                    <div>
                      <div style={label}>User ID (שיוך הייבוא)</div>
                      <input
                        value={userId}
                        onChange={(e) => {
                          const v = e.target.value;
                          setUserId(v);
                          localStorage.setItem("import_ads_user_id", v);
                        }}
                        inputMode="numeric"
                        placeholder="1"
                        style={inputLtr}
                      />


                      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" style={btnBase} onClick={() => copyToClipboard(urlDefault, setCopiedUrl)}>
                          {copiedUrl ? "✅ הועתק" : "העתק URL"}
                        </button>
                        <button type="button" style={btnBase} onClick={() => copyToClipboard(urlTest5NoImages, setCopiedUrl)}>
                          {copiedUrl ? "✅ הועתק" : `העתק טסט ${TEST_LIMIT}`}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Output */}
                <div style={sectionBorderTop}>
                  {error && (
                    <div style={notice("error")}>
                      <div style={{ fontWeight: 950, marginBottom: 6 }}>שגיאה</div>
                      <div style={{ fontSize: 12, color: "#7f1d1d", whiteSpace: "pre-wrap", direction: "ltr", textAlign: "left" }}>
                        {error}
                      </div>
                      <div style={{ marginTop: 10, fontSize: 12, color: "#991b1b" }}>
                        טיפ: אם זה 401/403 — בדקי שה־X-Admin-Key נכון. אם זה CORS — בדקי Network/Console.
                      </div>
                    </div>
                  )}

                  {cliRunning && !loading && (
                    <div style={notice("warn")}>
                      <div style={{ fontWeight: 950, marginBottom: 4 }}>יבוא כבר רץ עכשיו</div>
                      <div>ניסיון הפעלה בזמן שעדיין רץ יבוא. תן לו לסיים ונסה שוב.</div>
                    </div>
                  )}

                  {cliStarted && !loading && (
                    <div style={notice("success")}>
                      <div style={{ fontWeight: 950, marginBottom: 4 }}>יבוא הופעל בהצלחה</div>
                      <div>הסקריפט רץ ברקע וישלח מייל סיכום כשיסיים. אין צורך להמתין בדף הזה.</div>
                    </div>
                  )}

                  {result && (
                    <div style={notice("success")}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 950 }}>סיכום הרצה</div>

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            style={btnBase}
                            onClick={() => copyToClipboard(JSON.stringify(result, null, 2), setCopiedTech)}
                          >
                            {copiedTech ? "✅ הועתק" : "העתק JSON"}
                          </button>

                          <button type="button" style={btnBase} onClick={() => setShowTech((p) => !p)}>
                            {showTech ? "הסתר פרטים טכניים" : "הצג פרטים"}
                          </button>
                        </div>
                      </div>

                      <div style={divider} />

                      {/* KPI */}
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <KPI label="סה״כ" value={fmtNum(normalized?.summary?.total)} />
                        <KPI label="נוספו" value={fmtNum(normalized?.summary?.inserted)} />
                        <KPI label="עודכנו" value={fmtNum(normalized?.summary?.updated)} />
                        <KPI label="דולגו" value={fmtNum(normalized?.summary?.skipped)} />
                        <KPI label="שגיאות" value={fmtNum(normalized?.summary?.errorsCount)} />
                        <KPI label="זמן" value={meta?.tookMs ? formatSeconds(meta.tookMs) : "—"} />
                        <KPI label="Limit" value={meta?.limit ? fmtNum(meta.limit) : "—"} />
                      </div>

                      {/* Images KPI if exists */}
                      {normalized?.images && (
                        <>
                          <div style={divider} />
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            <KPI label="תמונות נוספו" value={fmtNum(normalized.images.inserted)} />
                            <KPI label="תמונות דולגו" value={fmtNum(normalized.images.skipped)} />
                            <KPI label="תמונות עם שגיאה" value={fmtNum(normalized.images.errors)} />
                          </div>
                        </>
                      )}

                      {/* Gallery */}
                      <ImageGrid urls={imageUrls} />

                      {/* Errors */}
                      <ErrorTable rows={normalized?.errors} />

                      {/* Items */}
                      <ItemCards items={normalized?.items} />

                      {/* Tech */}
                      {showTech && (
                        <>
                          <div style={divider} />
                          <div style={{ fontSize: 13, fontWeight: 950, color: "#0f172a", marginBottom: 10 }}>
                            פרטים (JSON)
                          </div>
                          <pre
                            style={{
                              margin: 0,
                              borderRadius: 16,
                              border: "1px solid #e5e7eb",
                              background: "#fff",
                              padding: 12,
                              maxHeight: 420,
                              overflow: "auto",
                              direction: "ltr",
                              textAlign: "left",
                              whiteSpace: "pre-wrap",
                              fontSize: 12,
                              color: "#0f172a",
                            }}
                          >
                            {JSON.stringify(result, null, 2)}
                          </pre>

                          {meta?.url && (
                            <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
                              URL שבוצעה: <span style={{ direction: "ltr" }}>{meta.url}</span>
                            </div>
                          )}
                          {meta?.mode && (
                            <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                              מצב: <b style={{ color: "#0f172a" }}>{meta.mode === "no_images" ? "ללא תמונות" : "כולל תמונות"}</b>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Suspense>
          </div>
        </div>
      </div>
    </>
  );
}
